import { Heading, ErrorBoundary, VStack, Box } from '@genial/components';
import { BoxRadius } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { RouteProp, useRoute } from '@react-navigation/native';
import { View } from 'native-base';
import { useEffect, useState } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import { useRecoilValue } from 'recoil';

import { ScrollCloseToBottomProvider } from '../../contexts/ScrollCloseToBottom';
import { useRecoilQuery } from '../../hooks/useRecoilQuery';
import { useScrollCloseToBottom } from '../../hooks/useScrollCloseToBottom';
import { SignedInRootStackParamList } from '../../routes/types';
import { isExportingPdfForCustomers } from '../../store/customer/atom';
import { groupedCustomerFinancialQuery } from '../../store/customer/selector';
import { CustomerV4FinancialResponseNormalized } from '../../store/customer/types';
import { masterStateSelectedAdvisorSelector } from '../../store/master/selector';

import { CustomerList } from './components/CustomerList';
import { CustomerSearchBar } from './components/CustomerSearchBar';

const styles = StyleSheet.create({
  contentComponentStyle: { flex: 1 },
});

export const Customers = () => {
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const selectedAdvisor = useRecoilValue(masterStateSelectedAdvisorSelector);

  const isExportingPdfData = useRecoilValue(isExportingPdfForCustomers);
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'Customers'>>();
  const [searchTerm, setSearchTerm] = useState<string>(params?.search || '');
  const [page, setPage] = useState<number>(1);
  const [loadingComplete, setLoadingComplete] = useState<boolean>(false);
  const [allCustomers, setAllCustomers] = useState<CustomerV4FinancialResponseNormalized[]>([]);

  const { isCloseToBottom, validateIsCloseToBottom, preventValidationByAddingDelay } =
    useScrollCloseToBottom();

  const { data, error, loading, refresher } = useRecoilQuery(
    groupedCustomerFinancialQuery({ searchTerm, page, limit: 10 })
  );

  const handleSortingChanged = () => {
    setAllCustomers([]);
    setPage(1);
    setLoadingComplete(false);
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isExportingPdfData) {
        const confirmationMessage =
          'Existe um processo de exportação do PDF do IR em andamento. Sair agora irá cancelar o processo.';
        event.returnValue = confirmationMessage;
        return confirmationMessage;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isExportingPdfData]);

  const handleSearch = (currentSearchTerm: string) => {
    if (!currentSearchTerm) {
      return;
    }
    setAllCustomers([]);
    setLoadingComplete(false);
    setSearchTerm(currentSearchTerm);
    setPage(1);
  };

  const handleClear = () => {
    if (!searchTerm) {
      return;
    }
    setSearchTerm('');
    setPage(1);
    setLoadingComplete(false);
  };

  const handleEndReached = () => {
    if (!data?.meta.pagination.hasNext || !loadingComplete || loading || error) {
      return;
    }

    setPage((prev) => prev + 1);
  };

  useEffect(() => {
    if (!data?.data) {
      return;
    }

    if (page === 1) {
      setAllCustomers(data.data);
    } else {
      setAllCustomers((prev) => [...prev, ...data.data]);
    }
    setLoadingComplete(true);
    preventValidationByAddingDelay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data]);

  useEffect(() => {
    setAllCustomers([]);
    setPage(1);
    setLoadingComplete(false);
  }, [selectedAdvisor]);

  useEffect(() => {
    if (loading) {
      setLoadingComplete(false);
    }
  }, [loading]);

  const renderPrincipal = () => {
    if (error) {
      return <ErrorBoundary onRefresh={refresher} />;
    } else {
      return (
        <VStack
          paddingY={{ base: 0, sm: 'inset.sm' }}
          space={isMobile ? 'stack.xxxs' : 'stack.xxs'}
          flex={1}>
          <CustomerSearchBar
            onClear={handleClear}
            onSearch={handleSearch}
            isLoading={!loadingComplete}
          />
          <CustomerList
            currentPage={page}
            customers={allCustomers}
            isLoading={!loadingComplete}
            isLoadingMore={!loadingComplete && page > 1}
            onEndReached={handleEndReached}
            onSortingChanged={handleSortingChanged}
            hasFilter={!!searchTerm}
            hasNextPage={data?.meta.pagination.hasNext || false}
          />
        </VStack>
      );
    }
  };

  return (
    <ScrollView
      style={styles.contentComponentStyle}
      showsHorizontalScrollIndicator={false}
      scrollEventThrottle={200}
      onScroll={({ nativeEvent }) => validateIsCloseToBottom(nativeEvent)}>
      <ScrollCloseToBottomProvider isCloseToBottom={isCloseToBottom}>
        <View
          p="inset.xs"
          flex={1}
          width="full"
          maxWidth={{ base: '100%', sm: '95%', lg: '1280px' }}
          minWidth={0}>
          <VStack space={isMobile ? 'stack.xxxs' : 'stack.xxs'} flex={1}>
            <Heading testId="heading" size="h2">
              Meus clientes
            </Heading>

            <Box
              backgroundColor={isMobile ? undefined : 'neutral.white'}
              borderRadius={BoxRadius.sm}
              flex={1}>
              {renderPrincipal()}
            </Box>
          </VStack>
        </View>
      </ScrollCloseToBottomProvider>
    </ScrollView>
  );
};
